import { useQuery, useZero } from "@corpuscomplete/zero-data-transformer/react";
import z from "zod/v4";
import { Draft } from "@corpuscomplete/core/common/draft";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { mutators, queries } from "@corpuscomplete/core/zero";
import { CurrentActor } from "../../actor";
import { AppForm } from "../../form";
import type { ZodType } from "zod/v4";

export namespace DraftUI {
  const SYNC_INTERVAL = 2000;

  type DraftFormDefaultValue<T> = () => T;

  export function useDraftForm<
    TValue extends Draft.Info["data"],
    TStorageSchema extends z.ZodType<TValue, TValue>,
    TValidationSchema extends z.ZodType<TValue, any>,
  >({
    type,
    onSubmit,
    draftKey,
    defaultValue,
    schemas,
  }: {
    type: string;
    onSubmit: (props: { value: TValue }) => Promise<void> | void;
    draftKey?: string;
    defaultValue: DraftFormDefaultValue<TValue>;
    schemas: {
      storage: TStorageSchema;
      validation: TValidationSchema;
    };
  }) {
    const actor = CurrentActor.use();
    const draftType = draftKey ? `${type}.${draftKey}` : type;
    const initialValue = defaultValue();

    const z = useZero();
    const [_, details] = useQuery(queries.drafts());
    const [draft] = useQuery(Draft.byType(z, draftType));
    const [defaultValues, setDefaultValues] = useState<TValue | null>(null);
    const [isSyncing, setIsSyncing] = useState(false);
    const pendingData = useRef<TValue>(null);
    const initializedRef = useRef(false);
    const previousTypeRef = useRef(draftType);
    const valuesRef = useRef<TValue>(null);

    useEffect(() => {
      if (previousTypeRef.current === draftType) {
        return;
      }
      previousTypeRef.current = draftType;
      initializedRef.current = false;
      pendingData.current = null;
      valuesRef.current = null;
      setDefaultValues(null);
      setIsSyncing(false);
    }, [draftType]);

    const form = AppForm.useAppForm({
      formId: draftType,
      defaultValues: defaultValues ?? initialValue,
      validators: { onSubmit: schemas.validation },
      onSubmit: async (props) => {
        await onSubmit(props);
        clear();
      },
    });

    useEffect(() => {
      if (details.type === "complete" && !initializedRef.current) {
        const parsedData = schemas.storage.safeParse(draft?.data).data;
        setDefaultValues(parsedData ?? initialValue);
        initializedRef.current = true;
      }
    }, [details.type, draft?.data, initialValue, schemas.storage]);

    const sync = useCallback(() => {
      if (pendingData.current === null) {
        return;
      }
      z.mutate(
        mutators.draft.set({
          userId: actor.userId,
          type: draftType,
          data: pendingData.current,
        }),
      );
      setDefaultValues(pendingData.current);
      pendingData.current = null;
      setIsSyncing(false);
    }, [actor.userId, z.mutate, draftType]);

    // oxlint-disable-next-line react-hooks/exhaustive-deps -- should run once per form instance
    useEffect(() => {
      return form.store.subscribe(({ currentVal }) => {
        if (!initializedRef.current) {
          return;
        }
        if (valuesRef.current === currentVal.values) {
          return;
        }
        valuesRef.current = currentVal.values;

        if (form.state.isPristine) {
          return;
        }
        pendingData.current = currentVal.values;
        setIsSyncing(true);
      });
    }, [form]);

    useEffect(() => {
      const interval = setInterval(() => {
        if (!initializedRef.current) {
          return;
        }
        sync();
      }, SYNC_INTERVAL);

      return () => clearInterval(interval);
    }, [sync]);

    const resetTo = useCallback(
      (value: TValue) => {
        z.mutate(
          mutators.draft.set({
            userId: actor.userId,
            type: draftType,
            data: value,
          }),
        );
        initializedRef.current = true;
        pendingData.current = null;
        valuesRef.current = value;
        setDefaultValues(value);
        setIsSyncing(false);
        form.reset(value);
      },
      [actor.userId, draftType, form, z.mutate],
    );

    const clear = useCallback(() => {
      z.mutate(mutators.draft.reset({ userId: actor.userId, type: draftType }));
      initializedRef.current = false;
      pendingData.current = null;
      valuesRef.current = null;
      setDefaultValues(null);
      setIsSyncing(false);
      form.reset();
    }, [actor.userId, form, draftType, z.mutate]);

    const draftExists = Boolean(draft) || pendingData.current !== null;

    return useMemo(
      () => ({ form, draftExists, clear, resetTo, isSyncing }),
      [form, draftExists, clear, resetTo, isSyncing],
    );
  }

  export function useDraft<T extends Draft.Info["data"]>(
    userId: string,
    type: string,
    schema: ZodType<T>,
    defaultValue: () => T,
  ) {
    const z = useZero();
    const [_, details] = useQuery(queries.drafts());
    const [draft] = useQuery(Draft.byType(z, type));
    const [exists, setExists] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const stateInitialized = useRef(false);
    const [state, setState] = useState<T>(defaultValue);

    // oxlint-disable-next-line react-hooks/exhaustive-deps -- should run once
    useEffect(() => {
      if (details.type === "complete" && !stateInitialized.current) {
        stateInitialized.current = true;
        const parsedData = draft && schema.safeParse(draft.data).data;
        if (parsedData) {
          setExists(true);
          setState(parsedData);
        }
      }
    }, [details.type]);

    const pendingData = useRef<T>(null);

    const set = useCallback((data: T) => {
      setState(data);
      pendingData.current = data;
      setExists(true);
      setIsSyncing(true);
    }, []);

    const draftExists = Boolean(draft);
    const reset = useCallback(() => {
      setState(defaultValue());
      pendingData.current = null;
      setExists(false);
      setIsSyncing(false);

      if (draftExists) {
        z.mutate(
          mutators.draft.reset({
            userId,
            type,
          }),
        );
      }
    }, [draftExists, z.mutate, userId, type, defaultValue]);

    useEffect(() => {
      const interval = setInterval(() => {
        if (pendingData.current !== null) {
          z.mutate(
            mutators.draft.set({
              userId,
              type,
              data: pendingData.current,
            }),
          );
          pendingData.current = null;
          setIsSyncing(false);
        }
      }, SYNC_INTERVAL);

      return () => clearInterval(interval);
    }, [type, userId, z.mutate]);

    return useMemo(
      () => ({ state, set, reset, exists, isSyncing }),
      [state, set, reset, exists, isSyncing],
    );
  }
}
